import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Pencil, Trash2, Search, Radio, Upload } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import CsvImportDialog from "../components/CsvImportDialog";
import { usePermissions } from "../hooks/usePermissions";

const PANEL_TYPES = ["Conventional", "Addressable", "Repeater", "MCP"];
const SYSTEM_TYPES = [
  "Hydrant System",
  "Sprinkler System",
  "Detection (Conventional)",
  "Detection (Addressable)",
  "Ventilation System",
  "Combined (Hydrant + Sprinkler)",
  "Break Glass / MCP",
  "Other",
];
const PANEL_TEMPLATE = [
  "panel_name,site_id,panel_type,zone,floor_number,serial_number,ip_address,licensed_agency,certifying_engineer",
  "Main Fire Panel,site_xxx,Addressable,Zone-1,1,SN12345,192.168.1.10,SafeGuard Pvt Ltd,Eng. Patil",
  "Repeater Panel 2,site_xxx,Repeater,Zone-2,2,SN67890,192.168.1.11,SafeGuard Pvt Ltd,Eng. Patil",
];

export default function PanelsManagement() {
  const { can } = usePermissions();
  const { sites } = useSiteContext();
  const [panels, setPanels] = useState([]);
  const [filter, setFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [drawerPanel, setDrawerPanel] = useState(null);
  const [pingResult, setPingResult] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);

  const load = () => api.get("/panels").then((r) => setPanels(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.panel_name || !payload.site_id || !payload.zone || !payload.floor_number) {
        toast.error("Fill all required fields"); return;
      }
      if (editing) {
        await api.patch(`/panels/${editing.panel_id}`, payload);
        toast.success("Panel updated");
      } else {
        await api.post("/panels", payload);
        toast.success("Panel added");
      }
      setOpen(false); setEditing(null); setForm({}); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete panel?")) return;
    await api.delete(`/panels/${id}`);
    toast.success("Deleted"); load();
  };

  const filtered = panels.filter((p) =>
    (siteFilter === "all" || p.site_id === siteFilter) &&
    (!filter || p.panel_name?.toLowerCase().includes(filter.toLowerCase()) || p.panel_id?.toLowerCase().includes(filter.toLowerCase()))
  );

  const statusColor = (s) =>
    s === "Working" ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
    : s === "Alarm" ? "bg-red-50 border border-red-200 text-red-700 status-pulse-red"
    : s === "Fault" ? "bg-yellow-500/20 text-yellow-700"
    : "bg-slate-100 border border-slate-200 text-slate-700";

  return (
    <div className="space-y-6" data-testid="panels-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500">Dashboard → Panels</div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Manage Fire Panels</h1>
        </div>
        <div className="flex gap-2">
        {can("panels_management", "create") && (
          <Button onClick={() => { setEditing(null); setForm({ panel_type: "Addressable" }); setOpen(true); }} className="f360-gradient border-0" data-testid="add-panel-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add New Panel
          </Button>
        )}
        {can("panels_management", "create") && (
          <Button onClick={() => setCsvOpen(true)} variant="outline" className="border-gray-200" data-testid="import-panels-btn">
            <Upload className="w-4 h-4 mr-1" strokeWidth={2.5} /> Import CSV
          </Button>
        )}
        </div>
      </div>

      <div className="glass p-4 rounded-lg flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by ID or name…" className="pl-10 bg-gray-50 border-gray-200" data-testid="panels-search" />
        </div>
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger className="w-56 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All Sites</SelectItem>
            {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="glass rounded-lg overflow-hidden">
        <div className="table-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead>Panel ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>Zone</TableHead>
              <TableHead>Floor</TableHead>
              <TableHead>Last Comm</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.panel_id} className="border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => setDrawerPanel(p)} data-testid={`panel-row-${p.panel_id}`}>
                <TableCell className="font-mono text-xs text-orange-700">{p.panel_id}</TableCell>
                <TableCell className="font-semibold">{p.panel_name}</TableCell>
                <TableCell><Badge variant="outline" className="border-gray-200">{p.panel_type}</Badge></TableCell>
                <TableCell className="text-xs">{sites.find((s) => s.site_id === p.site_id)?.name || p.site_id}</TableCell>
                <TableCell>{p.zone}</TableCell>
                <TableCell>{p.floor_number}</TableCell>
                <TableCell className="text-xs text-emerald-400">{p.last_communication ? new Date(p.last_communication).toLocaleTimeString("en-IN") : "—"}</TableCell>
                <TableCell><Badge className={`${statusColor(p.status)} border-0`}>{p.status}</Badge></TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {can("panels_management", "update") && <>
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setForm(p); setOpen(true); }} data-testid={`edit-${p.panel_id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(p.panel_id)} className="text-red-400" data-testid={`del-${p.panel_id}`}><Trash2 className="w-4 h-4" strokeWidth={2.5} /></Button>
                  </>}
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">No panels found.</TableCell></TableRow>}
          </TableBody>
        </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl" data-testid="panel-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{editing ? "Edit Panel" : "Add Panel"}</DialogTitle>
          </DialogHeader>

          {/* ROW 1 — System Type + Site (Location) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
            <div>
              <Label className="text-xs text-gray-500">System Type *</Label>
              <Select value={form.system_type || ""} onValueChange={(v) => setForm({ ...form, system_type: v })}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue placeholder="Select System Type" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  {SYSTEM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Location (Site) *</Label>
              <Select value={form.site_id || ""} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue placeholder="Select Location" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* ROW 2 — Panel IP Address + Panel Port No. */}
            <div>
              <Label className="text-xs text-gray-500">Panel IP Address</Label>
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="Panel IP Address" value={form.ip_address || ""} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Panel Port No.</Label>
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="Panel Port No." value={form.port_number || ""} onChange={(e) => setForm({ ...form, port_number: e.target.value })} />
            </div>

            {/* ROW 3 — Panel Name + Panel Type */}
            <div>
              <Label className="text-xs text-gray-500">Panel Name *</Label>
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Main Fire Panel" value={form.panel_name || ""} onChange={(e) => setForm({ ...form, panel_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Panel Type *</Label>
              <Select value={form.panel_type || "Addressable"} onValueChange={(v) => setForm({ ...form, panel_type: v })}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  {PANEL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* ROW 4 — Zone + Floor */}
            <div>
              <Label className="text-xs text-gray-500">Zone *</Label>
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Zone-1" value={form.zone || ""} onChange={(e) => setForm({ ...form, zone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Floor Number *</Label>
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 3" value={form.floor_number || ""} onChange={(e) => setForm({ ...form, floor_number: e.target.value })} />
            </div>

            {/* ROW 5 — Serial + Panel Detail (description) */}
            <div>
              <Label className="text-xs text-gray-500">Serial Number</Label>
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="Panel Serial No." value={form.serial_number || ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Licensed Agency</Label>
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. SafeGuard Pvt Ltd" value={form.licensed_agency || ""} onChange={(e) => setForm({ ...form, licensed_agency: e.target.value })} />
            </div>

            {/* ROW 6 — Panel Detail full width */}
            <div className="md:col-span-2">
              <Label className="text-xs text-gray-500">Panel Detail</Label>
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="Brief description of this panel and its coverage area" value={form.panel_detail || ""} onChange={(e) => setForm({ ...form, panel_detail: e.target.value })} />
            </div>

            {/* ROW 7 — Certifying Engineer + AMC Expiry */}
            <div>
              <Label className="text-xs text-gray-500">Certifying Engineer</Label>
              <Input className="bg-gray-50 border-gray-200 mt-1" value={form.certifying_engineer || ""} onChange={(e) => setForm({ ...form, certifying_engineer: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">AMC Expiry</Label>
              <Input type="date" className="bg-gray-50 border-gray-200 mt-1" value={form.amc_expiry || ""} onChange={(e) => setForm({ ...form, amc_expiry: e.target.value })} />
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={save} className="f360-gradient border-0" data-testid="save-panel-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drill-down drawer */}
      <Sheet open={!!drawerPanel} onOpenChange={(o) => !o && setDrawerPanel(null)}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-[480px] overflow-y-auto">
          {drawerPanel && (
            <>
              <SheetHeader className="border-b border-gray-200 pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-xs text-orange-700">{drawerPanel.panel_id}</span>
                  <Badge variant="outline" className="border-gray-200 text-xs">{drawerPanel.panel_type}</Badge>
                </div>
                <SheetTitle className="text-gray-900 text-2xl">{drawerPanel.panel_name}</SheetTitle>
                <div className="text-xs text-gray-500">{sites.find((s) => s.site_id === drawerPanel.site_id)?.name}</div>
              </SheetHeader>

              <div className="py-4 space-y-5">
                <section>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">Status</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Last Communication</div><div className="text-emerald-400 font-bold mt-0.5">{drawerPanel.last_communication ? new Date(drawerPanel.last_communication).toLocaleString("en-IN") : "—"}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Status</div><Badge className={`mt-0.5 border-0 ${statusColor(drawerPanel.status)}`}>{drawerPanel.status}</Badge></div>
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">IP Address</div><div className="font-mono mt-0.5">{drawerPanel.ip_address || "—"}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Firmware</div><div className="font-mono mt-0.5">v2.4.1</div></div>
                  </div>
                </section>

                <section>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">Zone Breakdown</div>
                  <div className="space-y-1.5">
                    {[1, 2, 3, 4].map((z) => (
                      <div key={z} className="bg-gray-50 rounded-lg p-2.5 flex items-center justify-between">
                        <div><div className="text-xs font-bold">Zone {z}</div><div className="text-[10px] text-gray-500">Floor {drawerPanel.floor_number} · 12 points</div></div>
                        <div className="flex items-center gap-2"><Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">Active</Badge><Button variant="ghost" size="sm" className="h-7 text-orange-400 text-xs">View</Button></div>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">7-Day Communication</div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <ResponsiveContainer width="100%" height={140}>
                      <LineChart data={Array.from({ length: 7 }, (_, i) => ({ d: `D${i + 1}`, uptime: 95 + ((i * 13) % 5) }))}>
                        <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                        <XAxis dataKey="d" stroke="#64748b" fontSize={10} />
                        <YAxis domain={[80, 100]} stroke="#64748b" fontSize={10} unit="%" />
                        <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
                        <Line type="monotone" dataKey="uptime" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </section>

                <section className="space-y-2">
                  {can("panels_management", "update") && (
                  <Button onClick={() => { setEditing(drawerPanel); setForm(drawerPanel); setDrawerPanel(null); setOpen(true); }} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800"><Pencil className="w-4 h-4 mr-2" />Edit Panel</Button>
                  )}
                  <Button
                    onClick={() => { setPingResult("pending"); setTimeout(() => { const ok = Math.random() > 0.15; setPingResult(ok ? "pass" : "fail"); ok ? toast.success("Ping PASS — panel responsive") : toast.error("Ping FAIL — no response"); }, 3000); }}
                    disabled={pingResult === "pending"}
                    className="w-full f360-gradient border-0" data-testid="ping-btn"
                  >
                    <Radio className="w-4 h-4 mr-2" strokeWidth={2.5} /> {pingResult === "pending" ? "Pinging…" : "Test Communication"}
                  </Button>
                  {can("panels_management", "delete") && (
                    <Button onClick={() => { if (window.confirm("Delete this panel?")) { del(drawerPanel.panel_id); setDrawerPanel(null); } }} className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-700"><Trash2 className="w-4 h-4 mr-2" strokeWidth={2.5} />Delete Panel</Button>
                  )}
                </section>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        title="Import Fire Panels from CSV"
        endpoint="/panels/bulk-import"
        templateRows={PANEL_TEMPLATE}
        templateFilename="panels_template.csv"
        onDone={load}
      />
    </div>
  );
}
